import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ACCOUNTS_BASE_URL, accountsHost, api, logout, type PhoneStatus } from "../lib/api";
import PlanSection from "../features/billing/PlanSection";
import { useAuth } from "../lib/auth";
import SessionUnavailable from "../components/SessionUnavailable";
import { siteHost } from "../lib/domains";
import {
  formatE164Pretty,
  formatPhoneDisplay,
  isValidUsPhone,
  toE164,
} from "../lib/phoneFormat";

function accountsSettingsUrl() {
  return `${ACCOUNTS_BASE_URL}/settings?next=${encodeURIComponent(window.location.href)}`;
}

export default function Account() {
  const auth = useAuth();
  if (auth.status === "error") return <SessionUnavailable retry={auth.retry} />;
  if (auth.status !== "authed") return null;
  const me = auth.me;
  const memberSince = me.memberSince ? new Date(me.memberSince) : null;

  return (
    <div className="account-page stack">
      <div className="welcome">
        <div className="studio-eyebrow">Account settings</div>
        <h1>Account</h1>
        <p>Profile, sites, plan, notifications, and security.</p>
      </div>

      {!me.emailVerified && (
        <div className="card stack" style={{ gap: 8, borderColor: "#fcd34d", background: "#fffbeb" }}>
          <div style={{ fontWeight: 700 }}>Verify your email</div>
          <div className="muted" style={{ fontSize: 13 }}>
            You can use the app now. Publishing, billing, and phone linking need a verified email.
          </div>
          <a className="btn primary" href={accountsSettingsUrl()} style={{ alignSelf: "flex-start" }}>
            Resend verification at {accountsHost()} →
          </a>
        </div>
      )}

      <SectionCard title="Profile" subtitle="Identity and contact email used across Prototypical.">
        <div className="account-grid">
          <Field label="Name" value={me.displayName || "—"} />
          <Field label="Email" value={
            <span className="row" style={{ gap: 6 }}>
              {me.email}
              {me.emailVerified
                ? <span className="badge good">verified</span>
                : <span className="badge warn">unverified</span>}
            </span>
          } />
          <Field label="Role" value={
            <span className={`badge ${me.role === "admin" ? "good" : me.role === "employee" ? "warn" : ""}`}>
              {me.role}
            </span>
          } />
          <Field label="Member since" value={memberSince ? memberSince.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—"} />
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          To change your name, email, or password, manage them at the central accounts hub.
        </div>
        <a className="btn" href={accountsSettingsUrl()} style={{ alignSelf: "flex-start" }}>
          Edit profile at {accountsHost()} →
        </a>
      </SectionCard>

      <PhoneChannelCard emailVerified={me.emailVerified} />

      <SectionCard title="Your sites" subtitle={
        me.siteStats.total === 0
          ? "No sites yet. Start one from the dashboard."
          : `${me.siteStats.total} site${me.siteStats.total === 1 ? "" : "s"} on this account.`
      }>
        <div className="account-stats">
          <Stat n={me.siteStats.total} label="Total" />
          <Stat n={me.siteStats.published} label="Published" tone="good" />
          <Stat n={me.siteStats.draft} label="Draft" />
          <Stat n={me.siteStats.building} label="Building" tone="warn" />
        </div>
        {me.sites.length > 0 && (
          <div className="stack" style={{ gap: 6, marginTop: 8 }}>
            {me.sites.slice(0, 5).map((s) => (
              <Link key={s.id} to={`/sites/${s.id}`} className="row between" style={{
                padding: "8px 12px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                color: "inherit",
                textDecoration: "none",
              }}>
                <span style={{ fontWeight: 500 }}>{siteHost(s.subdomain)}</span>
                <span className={`badge ${s.status === "active" ? "good" : s.status === "building" ? "warn" : ""}`}>
                  {s.status === "active" ? "live" : s.status}
                </span>
              </Link>
            ))}
          </div>
        )}
        <Link to="/dashboard" className="btn primary" style={{ alignSelf: "flex-start" }}>
          Go to dashboard
        </Link>
      </SectionCard>

      <PlanSection />

      <SectionCard title="Notifications" subtitle="Choose how ProtoPal keeps you informed.">
        <div className="row between" style={{ padding: "10px 0" }}>
          <div>
            <div style={{ fontWeight: 600 }}>Build and publishing updates</div>
            <div className="muted" style={{ fontSize: 13 }}>Important status email is currently managed automatically.</div>
          </div>
          <span className="badge good">On</span>
        </div>
        <div className="row between" style={{ padding: "10px 0", borderTop: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontWeight: 600 }}>Notification preferences</div>
            <div className="muted" style={{ fontSize: 13 }}>Self-serve preference controls are coming soon.</div>
          </div>
          <span className="coming-pill">Coming soon</span>
        </div>
      </SectionCard>

      <SectionCard title="Security" subtitle="Sign-in is managed centrally; sign out here when you're done.">
        <div className="stack" style={{ gap: 10 }}>
          <a href={accountsSettingsUrl()} className="row between" style={{
            padding: "10px 12px",
            border: "1px solid var(--border)",
            borderRadius: 8,
            color: "inherit",
            textDecoration: "none",
          }}>
            <div>
              <div style={{ fontWeight: 600 }}>Password & SMS login codes</div>
              <div className="muted" style={{ fontSize: 13 }}>Managed at {accountsHost()}</div>
            </div>
            <span className="muted">→</span>
          </a>
          <button onClick={() => void logout()} className="row between" style={{
            padding: "10px 12px",
            border: "1px solid var(--border)",
            borderRadius: 8,
            background: "var(--surface)",
            color: "var(--ink)",
            cursor: "pointer",
            font: "inherit",
            textAlign: "left",
            width: "100%",
          }}>
            <div>
              <div style={{ fontWeight: 600 }}>Sign out</div>
              <div className="muted" style={{ fontSize: 13 }}>End this session on this device.</div>
            </div>
            <span className="muted">→</span>
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Danger zone" subtitle="Account deletion and full data export are not yet self-serve.">
        <div className="muted" style={{ fontSize: 13 }}>
          Email <a href="mailto:dev@prototypical.ai" style={{ color: "var(--primary)", fontWeight: 600 }}>dev@prototypical.ai</a> from your account email and we'll handle it within one business day.
        </div>
      </SectionCard>
    </div>
  );
}

function PhoneChannelCard({ emailVerified }: { emailVerified: boolean }) {
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<PhoneStatus | null>(null);
  const [step, setStep] = useState<"idle" | "code">("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const phoneE164 = toE164(phoneDisplay);
  const phoneOk = Boolean(phoneE164 && isValidUsPhone(phoneDisplay));

  async function reload() {
    const r = await api.getPhone();
    setStatus(r);
    if (r.phone && !phoneDisplay) setPhoneDisplay(formatPhoneDisplay(r.phone));
  }

  useEffect(() => {
    reload().catch((e) => setError(String(e)));
  }, []);

  async function onStart(e: FormEvent) {
    e.preventDefault();
    if (!phoneE164) return;
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const r = await api.startPhoneVerify(phoneE164, password);
      setStep("code");
      setInfo(r.debugCode
        ? `Code sent (dev): ${r.debugCode}`
        : `Code sent to ${formatE164Pretty(r.phone)}. Expires in ${Math.round(r.expiresInSec / 60)} minutes.`);
      await reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.confirmPhoneVerify(code);
      setStep("idle");
      setCode("");
      setPassword("");
      setInfo("Phone verified. Used for optional login codes and text edits.");
      await reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onUnlink() {
    if (!password.trim()) {
      setError("Enter your password to unlink this phone.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.unlinkPhone(password);
      setStep("idle");
      setCode("");
      setPassword("");
      setPhoneDisplay("");
      setInfo("Phone unlinked.");
      await reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSmsTarget(siteId: string | null) {
    setBusy(true);
    setError("");
    try {
      await api.setSmsTarget(siteId);
      await reload();
      setInfo(siteId ? "SMS target site updated." : "SMS target cleared (latest updated site).");
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onToggleMfa(enable: boolean) {
    if (!password.trim()) {
      setError("Enter your password to change SMS login codes.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (enable) await api.enableMfa(password);
      else await api.disableMfa(password);
      setPassword("");
      setInfo(enable ? "SMS login codes enabled." : "SMS login codes disabled.");
      await reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  const twilioNumber = status?.twilioPublicNumber || null;

  return (
    <SectionCard
      title="Phone"
      subtitle="Used for optional login codes and text edits."
    >
      {!emailVerified && (
        <div className="badge warn" style={{ alignSelf: "flex-start" }}>
          Verify your email before linking a phone.
        </div>
      )}
      {status?.phoneVerified ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="row between" style={{ gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <strong>{formatE164Pretty(status.phone)}</strong>
                <span className="badge good">verified</span>
              </div>
              {twilioNumber ? (
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  Text edits to <strong>{twilioNumber}</strong>
                  <div style={{ marginTop: 4 }}>
                    Example: <code>change the headline to "Open late"</code> → open preview → reply <strong>YES</strong>
                  </div>
                </div>
              ) : (
                <div className="badge warn" style={{ marginTop: 6, alignSelf: "flex-start" }}>
                  Twilio number not published yet — ask ops to set TWILIO_FROM_NUMBER / TWILIO_PUBLIC_NUMBER.
                </div>
              )}
            </div>
          </div>

          {!status?.outboundConfigured && (
            <div className="badge warn" style={{ alignSelf: "flex-start" }}>
              Outbound SMS is not configured on this API — phone OTP / MFA texts will fail until TWILIO_* secrets are bound.
            </div>
          )}

          {(status.sites?.length ?? 0) > 0 && (
            <label className="stack" style={{ gap: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>SMS target site</span>
              <select
                value={status.smsTargetSiteId ?? ""}
                disabled={busy}
                onChange={(e) => void onSmsTarget(e.target.value || null)}
              >
                <option value="">Latest updated site</option>
                {status.sites!.map((s) => (
                  <option key={s.id} value={s.id}>{s.subdomain}</option>
                ))}
              </select>
              <span className="muted" style={{ fontSize: 12 }}>
                Or prefix a text with <code>site your-subdomain</code>
              </span>
            </label>
          )}

          <div className="row between" style={{ gap: 8, flexWrap: "wrap", padding: 10, border: "1px solid var(--border)", borderRadius: 8 }}>
            <div>
              <div style={{ fontWeight: 600 }}>Require a text code when I sign in</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {status.mfaSmsEnabled ? "On — login sends an SMS code" : "Off — password or Google only"}
              </div>
            </div>
            <button className="btn" type="button" disabled={busy} onClick={() => void onToggleMfa(!status.mfaSmsEnabled)}>
              {status.mfaSmsEnabled ? "Turn off" : "Turn on"}
            </button>
          </div>

          <label className="stack" style={{ gap: 4 }}>
            <span className="muted" style={{ fontSize: 12 }}>Confirm password to unlink or change MFA</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button className="btn" type="button" disabled={busy} onClick={onUnlink}>
            Unlink phone
          </button>
        </div>
      ) : (
        <>
          {!status?.outboundConfigured && (
            <div className="badge warn" style={{ alignSelf: "flex-start" }}>
              Outbound SMS not configured yet — ask ops to set TWILIO_* secrets.
            </div>
          )}
          {step === "idle" ? (
            <form className="stack" style={{ gap: 8 }} onSubmit={onStart}>
              <label className="stack" style={{ gap: 4 }}>
                <span className="muted" style={{ fontSize: 12 }}>Mobile number</span>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <span className="muted" style={{ fontSize: 14 }}>+1</span>
                  <input
                    value={phoneDisplay}
                    onChange={(e) => setPhoneDisplay(formatPhoneDisplay(e.target.value))}
                    placeholder="(555) 123-4567"
                    autoComplete="tel-national"
                    inputMode="tel"
                    required
                    style={{ flex: 1 }}
                  />
                </div>
              </label>
              <label className="stack" style={{ gap: 4 }}>
                <span className="muted" style={{ fontSize: 12 }}>Current password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              <button className="btn primary" type="submit" disabled={busy || !phoneOk || !password.trim() || !emailVerified}>
                Send code
              </button>
            </form>
          ) : (
            <form className="stack" style={{ gap: 8 }} onSubmit={onConfirm}>
              <label className="stack" style={{ gap: 4 }}>
                <span className="muted" style={{ fontSize: 12 }}>6-digit code</span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                />
              </label>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn primary" type="submit" disabled={busy || !code.trim()}>
                  Verify
                </button>
                <button className="btn" type="button" disabled={busy} onClick={() => setStep("idle")}>
                  Back
                </button>
              </div>
            </form>
          )}
        </>
      )}
      {info && <div className="muted" style={{ fontSize: 13 }}>{info}</div>}
      {error && <div className="badge bad">{error}</div>}
    </SectionCard>
  );
}

function SectionCard({
  title, subtitle, children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
        {subtitle && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone?: "good" | "warn" }) {
  const color = tone === "good" ? "#047857" : tone === "warn" ? "#b45309" : "var(--ink)";
  return (
    <div className="stat-card">
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{n}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{label}</div>
    </div>
  );
}
